import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import { mekakApi } from './services';
import type {
  AdvancedSearchResponse,
  QueryRecord,
  DashboardStats,
  Knowledge,
  CSVFile,
  CSVUploadResult,
  KnowledgeListPageState,
  FileDocument,
  FileProcessResult,
  FolderTreeNode,
  FolderContentsResponse,
  UploadQueueItem,
} from './models';
import { UPLOAD_QUEUE_MAX } from './models';

// ─── AI Search ──────────────────────────────────────────────────────────────

export function useAISearch() {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<AdvancedSearchResponse | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchMode, setSearchMode] = useState<'mixed' | 'pure_sql'>('mixed');

  const [history, setHistory] = useState<QueryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await mekakApi.getHistory();
      if (res.data.success && Array.isArray(res.data.data)) {
        setHistory(res.data.data);
      }
    } catch {
      // History is non-critical; fail silently
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) {
      message.warning('請輸入查詢問題後再搜尋');
      return;
    }

    setSearching(true);
    setResult(null);
    setHasSearched(true);

    try {
      const res = await mekakApi.search({ query: trimmed, force_mode: searchMode });
      if (res.data.success && res.data.data) {
        setResult(res.data.data);
        fetchHistory();
      } else {
        message.error('搜尋失敗，請稍後再試');
      }
    } catch (err: unknown) {
      const errMsg =
        err instanceof Error ? err.message : '網路錯誤，請確認後端服務是否正常';
      message.error(`搜尋錯誤：${errMsg}`);
    } finally {
      setSearching(false);
    }
  }, [query, searchMode, fetchHistory]);

  const handleHistoryClick = useCallback((historyQuery: string) => {
    setQuery(historyQuery);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return {
    query, setQuery,
    searching,
    result,
    hasSearched,
    searchMode, setSearchMode,
    history, historyLoading, fetchHistory,
    handleSearch,
    handleHistoryClick,
  } as const;
}

// ─── CSV Upload ─────────────────────────────────────────────────────────────

export function useCSVUpload() {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<CSVUploadResult | null>(null);
  const [csvFiles, setCsvFiles] = useState<CSVFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  const fetchCSVFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await mekakApi.listCSVFiles();
      if (response.data.success) {
        setCsvFiles(response.data.data ?? []);
      }
    } catch {
      message.error('無法載入已上傳的 CSV 檔案');
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    fetchCSVFiles();
  }, [fetchCSVFiles]);

  const openDrawer = useCallback((fileId: string) => {
    setSelectedFileId(fileId);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleRevectorize = useCallback(async (fileId: string) => {
    try {
      const res = await mekakApi.revectorizeCSVFile(fileId);
      if (res.data.success) {
        message.success('已觸發重新向量化，背景處理中');
        setDrawerOpen(false);
        fetchCSVFiles();
      } else {
        message.error('重新向量化失敗');
      }
    } catch {
      message.error('重新向量化時發生錯誤');
    }
  }, [fetchCSVFiles]);

  const handleDeleteCSV = useCallback(async (fileId: string) => {
    try {
      const res = await mekakApi.deleteCSVFile(fileId);
      if (res.data.success) {
        message.success('已刪除 CSV 檔案');
        setDrawerOpen(false);
        setSelectedFileId(null);
        fetchCSVFiles();
      } else {
        message.error('刪除失敗');
      }
    } catch {
      message.error('刪除時發生錯誤');
    }
  }, [fetchCSVFiles]);

  const handleDownloadAll = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await mekakApi.downloadAllCSV();
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'csv_export.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('下載完成');
    } catch {
      message.error('下載失敗');
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleUpload = useCallback(async (
    file: File,
    onSuccess?: (body: unknown) => void,
    onError?: (err: Error) => void,
    onProgress?: (event: { percent: number }) => void,
  ) => {
    const name = file.name.toLowerCase();
    const isValid =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      name.endsWith('.csv') ||
      name.endsWith('.xlsx') ||
      name.endsWith('.xls');

    if (!isValid) {
      message.error('僅接受 CSV 或 Excel (.xlsx/.xls) 檔案');
      onError?.(new Error('Invalid file type'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setUploadResult(null);

    try {
      onProgress?.({ percent: 30 });
      const response = await mekakApi.uploadCSV(formData);
      onProgress?.({ percent: 100 });

      if (response.data.success) {
        setUploadResult(response.data.data);
        message.success(`${file.name} 上傳成功`);
        onSuccess?.(response.data);
        fetchCSVFiles();
      } else {
        throw new Error(response.data.message ?? '上傳失敗');
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : '上傳時發生錯誤';
      message.error(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setUploading(false);
    }
  }, [fetchCSVFiles]);

  return {
    uploading, uploadResult,
    csvFiles, loadingFiles,
    downloading,
    drawerOpen, selectedFileId,
    fetchCSVFiles,
    openDrawer, closeDrawer,
    handleRevectorize, handleDeleteCSV, handleDownloadAll, handleUpload,
  } as const;
}

// ─── Knowledge List ─────────────────────────────────────────────────────────

export function useKnowledgeList() {
  const [state, setState] = useState<KnowledgeListPageState>({
    knowledgeList: [],
    loading: false,
    search: '',
    selectedTags: [],
    allTags: [],
    pagination: { current: 1, pageSize: 20, total: 0 },
  });

  const loadKnowledge = useCallback(
    async (overrides?: { search?: string; tags?: string[]; page?: number; pageSize?: number }) => {
      setState(prev => ({ ...prev, loading: true }));

      const search = overrides?.search ?? state.search;
      const tags = overrides?.tags ?? state.selectedTags;
      const page = overrides?.page ?? state.pagination.current;
      const pageSize = overrides?.pageSize ?? state.pagination.pageSize;

      try {
        const response = await mekakApi.listKnowledge({
          page,
          limit: pageSize,
          search: search || undefined,
          tag: tags.length === 1 ? tags[0] : undefined,
        });

        const data: Knowledge[] = response.data?.data ?? [];
        const total: number = response.data?.total ?? data.length;

        const tagSet = new Set<string>();
        data.forEach(item => item.tags?.forEach(t => tagSet.add(t)));

        setState(prev => ({
          ...prev,
          loading: false,
          knowledgeList: data,
          pagination: { ...prev.pagination, current: page, pageSize, total },
          allTags: Array.from(new Set([...prev.allTags, ...Array.from(tagSet)])),
        }));
      } catch (error) {
        message.error(
          `載入知識列表失敗：${error instanceof Error ? error.message : '未知錯誤'}`
        );
        setState(prev => ({ ...prev, loading: false }));
      }
    },
    [state.search, state.selectedTags, state.pagination.current, state.pagination.pageSize]
  );

  useEffect(() => {
    loadKnowledge({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      search: value,
      pagination: { ...prev.pagination, current: 1 },
    }));
    loadKnowledge({ search: value, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKnowledge]);

  const handleTagFilter = useCallback((tags: string[]) => {
    setState(prev => ({
      ...prev,
      selectedTags: tags,
      pagination: { ...prev.pagination, current: 1 },
    }));
    loadKnowledge({ tags, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKnowledge]);

  const handleTableChange = useCallback((pagination: TablePaginationConfig) => {
    const page = pagination.current ?? 1;
    const pageSize = pagination.pageSize ?? 20;
    setState(prev => ({ ...prev, pagination: { ...prev.pagination, current: page, pageSize } }));
    loadKnowledge({ page, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKnowledge]);

  const handleDelete = useCallback((record: Knowledge) => {
    // Returns the modal instance — caller renders Modal.confirm in UI
    return {
      title: '確認刪除',
      content: `確定要刪除知識「${record.title}」嗎？此操作無法復原。`,
      onOk: async () => {
        try {
          await mekakApi.deleteKnowledge(record.id);
          message.success('知識刪除成功');
          loadKnowledge();
        } catch (error) {
          message.error(
            `刪除失敗：${error instanceof Error ? error.message : '未知錯誤'}`
          );
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKnowledge]);

  return {
    state,
    loadKnowledge,
    handleSearch,
    handleTagFilter,
    handleTableChange,
    handleDelete,
  } as const;
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function useDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await mekakApi.getDashboard();
      if (response.data?.success && response.data?.data) {
        setStats(response.data.data);
      } else {
        message.error('取得統計資料失敗，伺服器回傳無效資料');
      }
    } catch {
      message.error('無法連線至 MEKAK 服務，請確認後端是否正常運作');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { loading, stats } as const;
}

// ─── Folder Tree ───────────────────────────────────────────────────────────

export function useFolderTree() {
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [selectedFolderID, setSelectedFolderID] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [folderContents, setFolderContents] = useState<FolderContentsResponse | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingContents, setLoadingContents] = useState(false);
  const [contentsPagination, setContentsPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const loadTree = useCallback(async () => {
    setLoadingTree(true);
    try {
      const res = await mekakApi.getFolderTree();
      if (res.data.success) {
        setFolderTree(res.data.data ?? []);
      }
    } catch {
      message.error('無法載入資料夾樹');
    } finally {
      setLoadingTree(false);
    }
  }, []);

  const loadContents = useCallback(async (folderID: string | null, page?: number, pageSize?: number) => {
    setLoadingContents(true);
    const p = page ?? contentsPagination.current;
    const ps = pageSize ?? contentsPagination.pageSize;
    try {
      const res = await mekakApi.getFolderContents({
        folder_id: folderID ?? undefined,
        limit: ps,
        offset: (p - 1) * ps,
      });
      if (res.data.success) {
        setFolderContents(res.data.data);
        setContentsPagination(prev => ({
          ...prev,
          current: p,
          pageSize: ps,
          total: res.data.data.total_knowledge,
        }));
      }
    } catch {
      message.error('無法載入資料夾內容');
    } finally {
      setLoadingContents(false);
    }
  }, [contentsPagination.current, contentsPagination.pageSize]);

  const selectFolder = useCallback((folderID: string | null) => {
    setSelectedFolderID(folderID);
    setContentsPagination(prev => ({ ...prev, current: 1 }));
    loadContents(folderID, 1);
  }, [loadContents]);

  const createFolder = useCallback(async (name: string, parentID?: string | null) => {
    try {
      const res = await mekakApi.createFolder({ name, parent_id: parentID });
      if (res.data.success) {
        message.success(`資料夾「${name}」已建立`);
        // Expand the parent so the new child is visible in the tree
        if (parentID) {
          setExpandedKeys(prev => prev.includes(parentID) ? prev : [...prev, parentID]);
        }
        await loadTree();
        // Always refresh contents so sub-folder grid updates
        loadContents(selectedFolderID);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '建立失敗';
      message.error(msg);
    }
  }, [loadTree, loadContents, selectedFolderID]);

  const renameFolder = useCallback(async (id: string, name: string) => {
    try {
      const res = await mekakApi.updateFolder(id, { name });
      if (res.data.success) {
        message.success('資料夾已重新命名');
        await loadTree();
        if (selectedFolderID === id) {
          loadContents(selectedFolderID);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '重新命名失敗';
      message.error(msg);
    }
  }, [loadTree, loadContents, selectedFolderID]);

  const deleteFolder = useCallback(async (id: string) => {
    try {
      const res = await mekakApi.deleteFolder(id);
      if (res.data.success) {
        message.success('資料夾已刪除');
        await loadTree();
        // If we deleted the currently selected folder, go to root
        if (selectedFolderID === id) {
          setSelectedFolderID(null);
          loadContents(null);
        } else {
          loadContents(selectedFolderID);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '刪除失敗';
      message.error(msg);
    }
  }, [loadTree, loadContents, selectedFolderID]);

  const moveItems = useCallback(async (knowledgeIDs: string[], fileDocIDs: string[], folderID: string | null) => {
    try {
      const res = await mekakApi.moveItemsToFolder({
        knowledge_ids: knowledgeIDs.length > 0 ? knowledgeIDs : undefined,
        file_doc_ids: fileDocIDs.length > 0 ? fileDocIDs : undefined,
        folder_id: folderID,
      });
      if (res.data.success) {
        message.success('項目已移動');
        await loadTree();
        loadContents(selectedFolderID);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '移動失敗';
      message.error(msg);
    }
  }, [loadTree, loadContents, selectedFolderID]);

  // Initial load
  useEffect(() => {
    loadTree();
    loadContents(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    folderTree, loadingTree,
    selectedFolderID, selectFolder,
    expandedKeys, setExpandedKeys,
    folderContents, loadingContents,
    contentsPagination, setContentsPagination,
    loadTree, loadContents,
    createFolder, renameFolder, deleteFolder,
    moveItems,
  } as const;
}

// ─── File Upload (PPT/PDF) ─────────────────────────────────────────────────

export function useFileUpload() {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<FileProcessResult | null>(null);
  const [fileDocuments, setFileDocuments] = useState<FileDocument[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const fetchFileDocuments = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const res = await mekakApi.listFileDocuments({ limit: 100 });
      if (res.data?.success) {
        setFileDocuments(res.data.data || []);
      }
    } catch {
      message.error('無法載入檔案列表');
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  const handleUpload = useCallback(async (
    file: File,
    onSuccess?: () => void,
    onError?: (err: Error) => void,
    folderID?: string | null,
  ) => {
    // Validate file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !['pptx', 'pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) {
      message.error('僅支援 .pptx、.pdf 和圖片（.png/.jpg/.webp/.gif）檔案');
      onError?.(new Error('Unsupported file type'));
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (folderID) {
        formData.append('folder_id', folderID);
      }

      const res = await mekakApi.uploadFile(formData);
      if (res.data?.success) {
        setUploadResult(res.data.data);
        const kc = res.data.data.knowledge_count;
        message.success(
          kc > 0
            ? `檔案已上傳：${res.data.data.page_count} 頁，${kc} 個知識點正在背景處理中，稍後重新整理即可看到`
            : `檔案已上傳，正在背景進行 OCR 與向量化處理...`
        );
        onSuccess?.();
        fetchFileDocuments();
      } else {
        message.error(res.data?.message || '檔案上傳失敗');
        onError?.(new Error(res.data?.message || 'Upload failed'));
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : '檔案上傳失敗';
      message.error(errorMsg);
      onError?.(new Error(errorMsg));
    } finally {
      setUploading(false);
    }
  }, [fetchFileDocuments]);

  const handleDeleteFile = useCallback(async (fileId: string) => {
    try {
      const res = await mekakApi.deleteFileDocument(fileId);
      if (res.data?.success) {
        message.success('檔案及相關知識點已刪除');
        fetchFileDocuments();
      } else {
        message.error('刪除失敗');
      }
    } catch {
      message.error('刪除失敗');
    }
  }, [fetchFileDocuments]);

  useEffect(() => {
    fetchFileDocuments();
  }, [fetchFileDocuments]);

  return {
    uploading,
    uploadResult,
    fileDocuments,
    loadingFiles,
    handleUpload,
    handleDeleteFile,
    fetchFileDocuments,
  } as const;
}

// ─── File Upload Queue (multi-file, serial POST) ──────────────────────────

const ALLOWED_UPLOAD_EXTS = ['pptx', 'pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif'];
/** Hard size cap for the signed-URL upload flow. Backend enforces 200MB; we
 * mirror that here so the user gets an immediate rejection rather than a
 * round-trip failure. */
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

function makeUid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Best-effort delete of a stuck placeholder file_documents row. Used when an
 * upload fails before the GCS object lands — without this, every failed
 * upload leaves a "uploading" row that pollutes the file list and confuses
 * users into thinking the file is being processed. */
async function tryDeletePlaceholder(id: string | null): Promise<void> {
  if (!id) return;
  try {
    await mekakApi.deleteFileDocument(id);
  } catch (err) {
    console.warn('[upload] failed to delete placeholder after upload failure:', err);
  }
}

/** PUT a File to a GCS V4 signed URL. Uses XHR (not fetch) so we can report
 * upload progress. Does NOT send our JWT — the signed URL is the auth. */
function putToGCS(
  signedURL: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<{ ok: boolean; status: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedURL, true);
    if (contentType) {
      xhr.setRequestHeader('Content-Type', contentType);
    }
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        onProgress(Math.round((ev.loaded / ev.total) * 100));
      }
    };
    xhr.onload = () => {
      // GCS returns 200 on success for signed URL PUT
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.send(file);
  });
}

/** Multi-file drag-and-drop upload queue.
 *
 * Uses the signed-URL flow to bypass App Engine's 32MB request limit:
 *   1. POST /files/upload-url      → backend issues a V4 signed URL
 *   2. PUT <signed_url>             → browser uploads bytes directly to GCS
 *   3. POST /files/:id/process      → backend reads from GCS and processes
 *
 * - Items run serially to avoid concurrent network thrashing.
 * - `submitted` means the bytes hit GCS and the backend acknowledged step 3.
 *   Real processing completion is observed via KnowledgeList's polling. */
export function useFileUploadQueue() {
  const [items, setItems] = useState<UploadQueueItem[]>([]);
  const itemsRef = useRef<UploadQueueItem[]>([]);
  const runningRef = useRef(false);

  const sync = useCallback((updater: (prev: UploadQueueItem[]) => UploadQueueItem[]) => {
    itemsRef.current = updater(itemsRef.current);
    setItems(itemsRef.current);
  }, []);

  const updateItem = useCallback((uid: string, patch: Partial<UploadQueueItem>) => {
    sync(prev => prev.map(it => (it.uid === uid ? { ...it, ...patch } : it)));
  }, [sync]);

  const runner = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const next = itemsRef.current.find(it => it.status === 'waiting');
        if (!next) break;

        updateItem(next.uid, { status: 'uploading', message: '取得上傳網址…' });

        // Track the placeholder file_documents row so we can clean it up if
        // the upload fails before the GCS object exists. Cleared once PUT
        // succeeds — at that point the file IS in GCS and the placeholder
        // should remain so processing can be retried.
        let placeholderID: string | null = null;
        try {
          // Step 1: get signed URL
          const urlRes = await mekakApi.createUploadURL({
            filename: next.file.name,
            content_type: next.file.type || 'application/octet-stream',
            file_size: next.file.size,
            folder_id: next.folderID,
          });
          if (!urlRes.data?.success) {
            updateItem(next.uid, {
              status: 'failed',
              message: urlRes.data?.error || '無法取得上傳網址',
            });
            continue;
          }
          const { file_doc_id, upload_url, content_type } = urlRes.data.data;
          placeholderID = file_doc_id;

          // Step 2: PUT bytes directly to GCS. Use fetch — apiClient would
          // attach our JWT, which GCS would reject; the signed URL IS the auth.
          updateItem(next.uid, { message: '上傳中 0%' });
          const putOk = await putToGCS(upload_url, next.file, content_type, (pct) => {
            updateItem(next.uid, { message: `上傳中 ${pct}%` });
          });
          if (!putOk.ok) {
            await tryDeletePlaceholder(placeholderID);
            placeholderID = null;
            updateItem(next.uid, {
              status: 'failed',
              message: `上傳到 GCS 失敗 (HTTP ${putOk.status})`,
            });
            continue;
          }
          // PUT succeeded — file is in GCS. Clear placeholderID so any
          // subsequent failure (Step 3, network) does NOT delete the row;
          // the user should be able to retry processing.
          placeholderID = null;

          // Step 3: kick off backend processing
          updateItem(next.uid, { message: '通知後端處理…' });
          const procRes = await mekakApi.startFileProcess(file_doc_id);
          if (!procRes.data?.success) {
            updateItem(next.uid, {
              status: 'failed',
              message: procRes.data?.error || '後端啟動處理失敗',
            });
            continue;
          }

          updateItem(next.uid, {
            status: 'submitted',
            message: '已送出，背景進行解析與向量化',
          });
        } catch (err: unknown) {
          // Network exception (PUT or createUploadURL throw). If the placeholder
          // was created but GCS object never landed, clean up so the file list
          // doesn't show a stuck "uploading" entry.
          await tryDeletePlaceholder(placeholderID);
          const errorMsg = err instanceof Error ? err.message : '上傳失敗';
          updateItem(next.uid, { status: 'failed', message: errorMsg });
        }
      }
    } finally {
      runningRef.current = false;
    }
  }, [updateItem]);

  /** Add files to the queue. Invalid extensions are pushed as `failed` so the
   * user sees what was rejected. Returns the number of items actually queued
   * (waiting or failed) — not the number that will eventually upload. */
  const enqueue = useCallback((files: File[], folderID?: string | null): number => {
    if (files.length === 0) return 0;

    const remaining = UPLOAD_QUEUE_MAX - itemsRef.current.length;
    if (remaining <= 0) {
      message.warning(`一次最多 ${UPLOAD_QUEUE_MAX} 個檔案，請先清理現有佇列`);
      return 0;
    }

    const accepted = files.slice(0, remaining);
    const dropped = files.length - accepted.length;
    if (dropped > 0) {
      message.warning(`已達 ${UPLOAD_QUEUE_MAX} 檔上限，後 ${dropped} 個檔案略過`);
    }

    const newItems: UploadQueueItem[] = accepted.map(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ext || !ALLOWED_UPLOAD_EXTS.includes(ext)) {
        return {
          uid: makeUid(),
          file,
          status: 'failed',
          message: '不支援的檔案格式',
          folderID,
        };
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        const mb = (file.size / (1024 * 1024)).toFixed(1);
        return {
          uid: makeUid(),
          file,
          status: 'failed',
          message: `檔案過大（${mb} MB），上限 ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB`,
          folderID,
        };
      }
      return {
        uid: makeUid(),
        file,
        status: 'waiting',
        folderID,
      };
    });

    sync(prev => [...prev, ...newItems]);
    void runner();
    return newItems.length;
  }, [sync, runner]);

  /** Remove a settled or waiting item from the queue. In-flight items are not
   * removable — abandoning them mid-POST would leave the request orphaned. */
  const removeItem = useCallback((uid: string) => {
    const target = itemsRef.current.find(it => it.uid === uid);
    if (!target || target.status === 'uploading') return;
    sync(prev => prev.filter(it => it.uid !== uid));
  }, [sync]);

  const reset = useCallback(() => {
    if (runningRef.current) return;
    sync(() => []);
  }, [sync]);

  const hasInflight = items.some(it => it.status === 'uploading' || it.status === 'waiting');
  const allSettled = items.length > 0 && items.every(
    it => it.status === 'submitted' || it.status === 'failed',
  );
  const submittedCount = items.filter(it => it.status === 'submitted').length;
  const failedCount = items.filter(it => it.status === 'failed').length;

  return {
    items,
    enqueue,
    removeItem,
    reset,
    hasInflight,
    allSettled,
    submittedCount,
    failedCount,
  } as const;
}
